import { useRouter } from 'next/router';
import React, { ReactNode, RefObject } from 'react';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { Space } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { RightColumnContent } from '../../../components/RightColumn';
import { AB_NAME } from '../../../constants/sitesettings';
import { useWhitelabel } from '../../../reducers/whitelabel';
import { ListContentLayout } from '../../Layouts/ListContentLayout';
import { Layout } from '../../Layouts/PageLayout';
import { ListGrid } from '../../ListGrid';
import { ReduxBanner } from '../../ReduxBanner';
import { SpecialOffersBlock } from '../../SpecialOffersBlock';
import { ListCard } from '../components/ListCard';
import { Preloader } from '../components/Preloader';
import { Trigger } from '../components/Trigger';
import { createListRenderer } from '../helpers/createListRenderer';
import { ListContainerProps, withListContainer } from '../hoc/withListContainer';

interface Props {
  heading?: React.ReactNode;
  betweenHeadingAndContent?: React.ReactNode;
  card?: React.ComponentProps<typeof ListCard>['card'];
  injectConfig?: Record<number, JSX.Element>;
  preloader?: React.ReactNode;
  withImages?: boolean;
  children?: ReactNode;
  triggerRef?: RefObject<HTMLElement>;
}

export const ListContainer: React.FC<Props & ListContainerProps> = ({
  loading,
  list,
  tabsForSpecialOffers,
  handleShowAuthPopup,
  heading,
  betweenHeadingAndContent,
  card,
  injectConfig: propsInjectConfig,
  triggerRef,
  preloader,
  children,
  withImages,
  createItemClickHandler,
}) => {
  const isMobile = useIsMobile();
  const isWhitelabel = useWhitelabel();
  const { asPath } = useRouter();

  const banner = <ReduxBanner lazy={true} abName={AB_NAME} type={BANNER_TYPES.TGBM_7} />;
  const injectConfig = propsInjectConfig || {
    1: !isWhitelabel && banner && (
      <ListGrid.Cell type="full" className={'h-mt-24 h-mb-24'}>
        {banner}
      </ListGrid.Cell>
    ),
    7: !!tabsForSpecialOffers.length && (
      <ListGrid.Cell type="full" className={isMobile && 'h-pr-16 h-pl-16'}>
        <SpecialOffersBlock tabs={tabsForSpecialOffers} className={'h-mt-24'} isMainPage />
      </ListGrid.Cell>
    ),
  };

  return (
    <Space size={16}>
      <ListContentLayout>
        {React.isValidElement(heading) && heading.type === ListContentLayout.Heading ? (
          heading
        ) : (
          <ListContentLayout.Heading>{heading}</ListContentLayout.Heading>
        )}
        {React.isValidElement(betweenHeadingAndContent) &&
        betweenHeadingAndContent.type === ListContentLayout.BetweenHeadingAndContent ? (
          betweenHeadingAndContent
        ) : (
          <ListContentLayout.BetweenHeadingAndContent>
            {betweenHeadingAndContent}
          </ListContentLayout.BetweenHeadingAndContent>
        )}
        {React.isValidElement(children) && children.type === ListContentLayout.Content ? (
          children
        ) : (
          <ListContentLayout.Content as={ListGrid}>
            {createListRenderer(
              list,
              (material, index) => {
                return (
                  <ListCard
                    wrapper={ListGrid.Cell}
                    index={index}
                    material={material}
                    // ** такой вид ключа очень важен для корректного отображения статей
                    key={`${material.alias}-${material.id}-${index}-${asPath}`}
                    handleShowAuthPopup={handleShowAuthPopup}
                    titleTag={index === 0 ? 'h1' : 'h2'}
                    card={card}
                    onClick={createItemClickHandler(index, material)}
                    as="article"
                    withImage={withImages}
                  />
                );
              },
              injectConfig,
            )}
            <Trigger ref={triggerRef} />
          </ListContentLayout.Content>
        )}
        <ListContentLayout.Foot>
          {preloader || <Preloader isLoading={loading} withImage={withImages} />}
        </ListContentLayout.Foot>
      </ListContentLayout>
      <Layout.RightSide>
        {!isWhitelabel && <RightColumnContent readOnBlockTitle={'Партнёрские материалы'} />}
      </Layout.RightSide>
    </Space>
  );
};

export const List = withListContainer()(ListContainer);
